import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import {
  ContactModel,
  findFirstEmailAddress,
  findFirstPhoneNumber,
} from '@akin-home/contacts';
import { useState } from 'react';

type InvitationMode = 'email' | 'sms';
interface State {
  status: 'loading' | 'ready' | 'sending_invitation';

  invitation_mode: InvitationMode;

  contact?: ContactModel;

  /** The currently selected email address */
  selected_email?: string;

  selected_phone_number?: string;

  new_email?: string;

  new_phone_number?: string;

  /** Custom invitation body that can be modified by user */
  invitation_body?: string;

  /** An error that has occured */
  error?: {
    code: string;
    message: string;
  };
}

const default_state: State = {
  status: 'loading',
  invitation_mode: 'email',
};

const getDefaultInvitationBody = (to_name = '') => {
  const from_name = useStateStore.getState().current_user?.name ?? '';

  return `Hi ${to_name},
Join the video call on my Hey Pixi Assistive Tablet.
${from_name}`;
};

export const useCreateVideoCallView = () => {
  const [state, setState] = useState<State>(default_state);

  const setContact = (contact: ContactModel) => {
    const new_state: State = {
      ...state,
      status: 'ready',
      contact,
    };

    const first_email_address: string | undefined =
      findFirstEmailAddress(contact);

    if (!first_email_address) {
      // Due to business requirements not supporting SMS, this has been commented out.
      // const first_phone_number = findFirstPhoneNumber(contact);
      // if (first_phone_number) {
      //   new_state.invitation_mode = 'sms';
      //   new_state.selected_phone_number = first_phone_number;
      // }
    } else {
      new_state.selected_email = first_email_address;
    }

    new_state.invitation_body = getDefaultInvitationBody(contact.name);

    setState(new_state);
  };

  const selectEmail = (selected_email: string) => {
    setState({
      ...state,
      selected_email,
    });
  };

  const selectPhoneNumber = (selected_phone_number: string) => {
    setState({
      ...state,
      selected_phone_number,
    });
  };

  const setNewEmail = (new_email: string) => {
    setState({
      ...state,
      new_email,
    });
  };

  const setNewPhoneNumber = (new_phone_number: string) => {
    setState({
      ...state,
      new_phone_number,
    });
  };

  const setInvitationBody = (invitation_body: string) => {
    setState({
      ...state,
      invitation_body,
    });
  };

  const setSendingInvitation = () => {
    setState({
      ...state,
      status: 'sending_invitation',
    });
  };

  const setError = (error: State['error']) => {
    setState({
      ...state,
      error,

      // Move back to ready if in sending invitation state
      status: state.status === 'sending_invitation' ? 'ready' : state.status,
    });
  };

  const setInvitationMode = (invitation_mode: InvitationMode) => {
    if (!state.contact) return;

    const new_state = { ...state, invitation_mode };

    if (invitation_mode === 'sms') {
      new_state.selected_email = undefined;
      new_state.new_email = undefined;

      const first_phone_number = findFirstPhoneNumber(state.contact);

      if (first_phone_number) {
        new_state.selected_phone_number = first_phone_number;
      }
    }

    if (invitation_mode === 'email') {
      new_state.selected_phone_number = undefined;
      new_state.new_phone_number = undefined;

      const first_email_address = findFirstEmailAddress(state.contact);

      if (first_email_address) {
        new_state.selected_email = first_email_address;
      }
    }

    setState(new_state);
  };

  return {
    state,
    sending_disabled: state.status === 'sending_invitation',
    setContact,
    selectEmail,
    setInvitationBody,
    setSendingInvitation,
    setError,
    setNewEmail,
    setInvitationMode,
    selectPhoneNumber,
    setNewPhoneNumber,
  };
};
export { InvitationMode as VideoCallInvitationMode };
